/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Stillinger-Weber parameter packs for the covalent 'sw' force field.
 * SW (Stillinger & Weber, Phys. Rev. B 31 (1985) 5262) is the canonical
 * 2-body + explicit-3-body potential for tetrahedral semiconductors —
 * the angular term penalises deviation from sp³ 109.47° and stabilizes
 * the diamond / zincblende / wurtzite structures that no pair-only or
 * EAM-style potential can hold.
 *
 * Parameters are stored per (i, j, k) triple, matching the LAMMPS .sw
 * file format directly (see StillingerWeberPotential's doc). For an
 * elemental pack one (X, X, X) triple suffices; for a binary, eight
 * triples over {A, B} are needed (A-A-A, A-A-B, A-B-A, A-B-B, B-A-A,
 * B-A-B, B-B-A, B-B-B) — paste them in directly from a .sw file.
 *
 * Standard sources, all validated against experimental cohesion at the
 * experimental lattice constant (ship only ≲ 10 %):
 *   - Si elemental: Stillinger & Weber 1985, J. Chem. Phys. 31 5262.
 *   - Ge elemental: Ding & Andersen, PRB 34 (1986) 6987.
 *   - CdTe, ZnS, ZnSe, ZnTe (and others through the same multi-element
 *     file): Zhou, Ward, Martin, van Swol, Cruz-Campa, Zubia, PRB 88
 *     (2013) 085309. Extracted from LAMMPS CdTeZnSeHgS0.sw.
 */
import { CrystalState } from '../../types.ts';
import { StillingerWeberParams, StillingerWeberPotential, ThreeBodyPotential } from './PairPotential.ts';

export interface SwPack {
  /** Literature source for the parameter set. */
  source: string;
  /** Build the ThreeBodyPotential for this composition. */
  build(): ThreeBodyPotential;
}

// ── Elemental (Si, Ge) — single (X, X, X) triple each ──

const SW_SI: StillingerWeberParams = {
  epsilon: 2.1683,
  sigma: 2.0951,
  A: 7.049556277,
  B: 0.6022245584,
  p: 4,
  q: 0,
  a: 1.8,
  lambda: 21.0,
  gamma: 1.2,
  cosTheta0: -1 / 3, // tetrahedral 109.47°
};

const SW_GE: StillingerWeberParams = {
  epsilon: 1.93,
  sigma: 2.181,
  A: 7.049556277,
  B: 0.6022245584,
  p: 4,
  q: 0,
  a: 1.8,
  lambda: 31.0, // slightly stronger angular constraint than Si
  gamma: 1.2,
  cosTheta0: -1 / 3,
};

// ── II-VI binaries (Zhou 2013) — 8 (i, j, k) triples per binary. ──
//    Pasted verbatim from LAMMPS CdTeZnSeHgS0.sw rows. The diagonal
//    rows (e3 = e2) carry the pair properties (ε_ij, σ_ij, A, B, p, q,
//    a, γ_ij); the off-diagonal rows (e3 ≠ e2) carry the per-(i, j, k)
//    triple λ_jik, ε_jik, cosθ₀.

// --- CdTe -------------------------------------------------------
const ZHOU13_CD_CD_CD: StillingerWeberParams = {
  epsilon: 1.182358,
  sigma: 2.663951,
  a: 1.527956,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.767446,
  p: 4,
  q: 0,
};
const ZHOU13_CD_CD_TE: StillingerWeberParams = {
  epsilon: 1.385284,
  sigma: 2.352141,
  a: 1.810919,
  lambda: 30.02537,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.886125,
  p: 4,
  q: 0,
};
const ZHOU13_CD_TE_CD: StillingerWeberParams = {
  epsilon: 1.182358,
  sigma: 2.663951,
  a: 1.527956,
  lambda: 35.17858,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.767446,
  p: 4,
  q: 0,
};
const ZHOU13_CD_TE_TE: StillingerWeberParams = {
  epsilon: 1.385284,
  sigma: 2.352141,
  a: 1.810919,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.886125,
  p: 4,
  q: 0,
};
const ZHOU13_TE_CD_CD: StillingerWeberParams = {
  epsilon: 1.385284,
  sigma: 2.352141,
  a: 1.810919,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.886125,
  p: 4,
  q: 0,
};
const ZHOU13_TE_CD_TE: StillingerWeberParams = {
  epsilon: 1.849775,
  sigma: 2.905254,
  a: 1.594353,
  lambda: 28.12506,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.730728,
  p: 4,
  q: 0,
};
const ZHOU13_TE_TE_CD: StillingerWeberParams = {
  epsilon: 1.385284,
  sigma: 2.352141,
  a: 1.810919,
  lambda: 37.55548,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.886125,
  p: 4,
  q: 0,
};
const ZHOU13_TE_TE_TE: StillingerWeberParams = {
  epsilon: 1.849775,
  sigma: 2.905254,
  a: 1.594353,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.730728,
  p: 4,
  q: 0,
};

// --- ZnS --------------------------------------------------------
const ZHOU13_ZN_ZN_ZN: StillingerWeberParams = {
  epsilon: 1.392961,
  sigma: 2.36765,
  a: 1.525521,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.767628,
  p: 4,
  q: 0,
};
const ZHOU13_ZN_ZN_S: StillingerWeberParams = {
  epsilon: 2.20839,
  sigma: 2.323783,
  a: 1.589241,
  lambda: 25.8116,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.464318,
  p: 4,
  q: 0,
};
const ZHOU13_ZN_S_ZN: StillingerWeberParams = {
  epsilon: 1.392961,
  sigma: 2.36765,
  a: 1.525521,
  lambda: 40.92153,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.767628,
  p: 4,
  q: 0,
};
const ZHOU13_ZN_S_S: StillingerWeberParams = {
  epsilon: 2.20839,
  sigma: 2.323783,
  a: 1.589241,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.464318,
  p: 4,
  q: 0,
};
const ZHOU13_S_ZN_ZN: StillingerWeberParams = {
  epsilon: 2.20839,
  sigma: 2.323783,
  a: 1.589241,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.464318,
  p: 4,
  q: 0,
};
const ZHOU13_S_ZN_S: StillingerWeberParams = {
  epsilon: 2.434871,
  sigma: 2.423171,
  a: 1.711097,
  lambda: 30.95161,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 1.049688,
  p: 4,
  q: 0,
};
const ZHOU13_S_S_ZN: StillingerWeberParams = {
  epsilon: 2.20839,
  sigma: 2.323783,
  a: 1.589241,
  lambda: 34.12585,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.464318,
  p: 4,
  q: 0,
};
const ZHOU13_S_S_S: StillingerWeberParams = {
  epsilon: 2.434871,
  sigma: 2.423171,
  a: 1.711097,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 1.049688,
  p: 4,
  q: 0,
};

// --- ZnSe -------------------------------------------------------
const ZHOU13_ZN_ZN_SE: StillingerWeberParams = {
  epsilon: 1.691181,
  sigma: 2.028827,
  a: 1.836907,
  lambda: 29.49563,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.951093,
  p: 4,
  q: 0,
};
const ZHOU13_ZN_SE_ZN: StillingerWeberParams = {
  epsilon: 1.392961,
  sigma: 2.36765,
  a: 1.525521,
  lambda: 35.81039,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.767628,
  p: 4,
  q: 0,
};
const ZHOU13_ZN_SE_SE: StillingerWeberParams = {
  epsilon: 1.691181,
  sigma: 2.028827,
  a: 1.836907,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.951093,
  p: 4,
  q: 0,
};
const ZHOU13_SE_ZN_ZN: StillingerWeberParams = {
  epsilon: 1.691181,
  sigma: 2.028827,
  a: 1.836907,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.951093,
  p: 4,
  q: 0,
};
const ZHOU13_SE_ZN_SE: StillingerWeberParams = {
  epsilon: 2.400781,
  sigma: 2.789002,
  a: 1.544925,
  lambda: 27.27735,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.767213,
  p: 4,
  q: 0,
};
const ZHOU13_SE_SE_ZN: StillingerWeberParams = {
  epsilon: 1.691181,
  sigma: 2.028827,
  a: 1.836907,
  lambda: 38.7226,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 0.951093,
  p: 4,
  q: 0,
};
const ZHOU13_SE_SE_SE: StillingerWeberParams = {
  epsilon: 2.400781,
  sigma: 2.789002,
  a: 1.544925,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.767213,
  p: 4,
  q: 0,
};

// --- ZnTe -------------------------------------------------------
const ZHOU13_ZN_ZN_TE: StillingerWeberParams = {
  epsilon: 1.546239,
  sigma: 2.056363,
  a: 1.907922,
  lambda: 30.84711,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.255846,
  p: 4,
  q: 0,
};
const ZHOU13_ZN_TE_ZN: StillingerWeberParams = {
  epsilon: 1.392961,
  sigma: 2.36765,
  a: 1.525521,
  lambda: 34.24146,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.767628,
  p: 4,
  q: 0,
};
const ZHOU13_ZN_TE_TE: StillingerWeberParams = {
  epsilon: 1.546239,
  sigma: 2.056363,
  a: 1.907922,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.255846,
  p: 4,
  q: 0,
};
const ZHOU13_TE_ZN_ZN: StillingerWeberParams = {
  epsilon: 1.546239,
  sigma: 2.056363,
  a: 1.907922,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.255846,
  p: 4,
  q: 0,
};
const ZHOU13_TE_ZN_TE: StillingerWeberParams = {
  epsilon: 1.849775,
  sigma: 2.905254,
  a: 1.594353,
  lambda: 29.71408,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.730728,
  p: 4,
  q: 0,
};
const ZHOU13_TE_TE_ZN: StillingerWeberParams = {
  epsilon: 1.546239,
  sigma: 2.056363,
  a: 1.907922,
  lambda: 35.54713,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.255846,
  p: 4,
  q: 0,
};

// --- HgS / HgTe (metacinnabar, coloradoite) ---------------------
// Both zincblende. β-HgS at a₀ = 5.85 Å, HgTe at a₀ = 6.46 Å.
// Hg-Hg-Hg is shared between the two packs (same constant).
const ZHOU13_HG_HG_HG: StillingerWeberParams = {
  epsilon: 1.272807,
  sigma: 2.699097,
  a: 1.498503,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 1.211532,
  p: 4,
  q: 0,
};

// HgS-specific rows
const ZHOU13_HG_HG_S: StillingerWeberParams = {
  epsilon: 1.531211,
  sigma: 2.025045,
  a: 1.833708,
  lambda: 29.63105,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.184541,
  p: 4,
  q: 0,
};
const ZHOU13_HG_S_HG: StillingerWeberParams = {
  epsilon: 1.272807,
  sigma: 2.699097,
  a: 1.498503,
  lambda: 35.64673,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 1.211532,
  p: 4,
  q: 0,
};
const ZHOU13_HG_S_S: StillingerWeberParams = {
  epsilon: 1.531211,
  sigma: 2.025045,
  a: 1.833708,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.184541,
  p: 4,
  q: 0,
};
const ZHOU13_S_HG_HG: StillingerWeberParams = {
  epsilon: 1.531211,
  sigma: 2.025045,
  a: 1.833708,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.184541,
  p: 4,
  q: 0,
};
const ZHOU13_S_HG_S: StillingerWeberParams = {
  epsilon: 2.434871,
  sigma: 2.423171,
  a: 1.711097,
  lambda: 25.77288,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 1.049688,
  p: 4,
  q: 0,
};
const ZHOU13_S_S_HG: StillingerWeberParams = {
  epsilon: 1.531211,
  sigma: 2.025045,
  a: 1.833708,
  lambda: 40.983,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.184541,
  p: 4,
  q: 0,
};

// HgTe-specific rows
const ZHOU13_HG_HG_TE: StillingerWeberParams = {
  epsilon: 1.204715,
  sigma: 2.135591,
  a: 1.892491,
  lambda: 33.40584,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.44518,
  p: 4,
  q: 0,
};
const ZHOU13_HG_TE_HG: StillingerWeberParams = {
  epsilon: 1.272807,
  sigma: 2.699097,
  a: 1.498503,
  lambda: 31.61872,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 1.211532,
  p: 4,
  q: 0,
};
const ZHOU13_HG_TE_TE: StillingerWeberParams = {
  epsilon: 1.204715,
  sigma: 2.135591,
  a: 1.892491,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.44518,
  p: 4,
  q: 0,
};
const ZHOU13_TE_HG_HG: StillingerWeberParams = {
  epsilon: 1.204715,
  sigma: 2.135591,
  a: 1.892491,
  lambda: 32.5,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.44518,
  p: 4,
  q: 0,
};
const ZHOU13_TE_HG_TE: StillingerWeberParams = {
  epsilon: 1.849775,
  sigma: 2.905254,
  a: 1.594353,
  lambda: 26.22805,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.917,
  B: 0.730728,
  p: 4,
  q: 0,
};
const ZHOU13_TE_TE_HG: StillingerWeberParams = {
  epsilon: 1.204715,
  sigma: 2.135591,
  a: 1.892491,
  lambda: 40.27176,
  gamma: 1.2,
  cosTheta0: -0.3333333,
  A: 7.0496,
  B: 1.44518,
  p: 4,
  q: 0,
};

// ── MoS₂ — Kandemir et al. (2016), modified SW for TMDs ──────────
// Sourced from github.com/KHISAMA/SW_potential_MoS2_lammps/MoS2.swtmd
// (the LAMMPS pair_sw_TMD parameter file). Uses the r23 third-leg
// cutoff: V₃ is zero if the (j, k) distance exceeds r23. This is
// what lets a single cosθ₀ ≈ 0.13 (82.5°) work for the trigonal
// prismatic Mo-S coordination — the cross-prism S-S pairs at ~4.78 Å
// (which would have a wrong angle of ~136°) are excluded by r23 = 3.82,
// leaving only the close in-triangle and eclipsed S-S pairs at 3.16-
// 3.17 Å (which all match the 82° preferred angle).
//
// For monolayer MoS₂: a₀ = 3.16 Å in-plane, Mo-S bond 2.41 Å, S-Mo-S
// angle 82.5°. Cohesion target ≈ 6.5 eV/atom (monolayer SW fits this
// jointly with phonon and mechanical properties).
const KANDEMIR16_MO_MO_MO: StillingerWeberParams = {
  // 2-body Mo-Mo only (no 3-body — lambda = 0). The Mo-Mo distance in
  // monolayer MoS₂ is the in-plane a = 3.16 Å, below the cutoff a·σ ≈
  // 4.27 Å, so Mo-Mo pair forces fire. Provides a soft Mo-Mo repulsion
  // to prevent Mo atoms from coming too close.
  epsilon: 1.0,
  sigma: 0.9999,
  a: 4.2706,
  lambda: 0,
  gamma: 0,
  cosTheta0: 0,
  A: 1.48668,
  B: 79.8272,
  p: 4,
  q: 0,
};
const KANDEMIR16_MO_S_S: StillingerWeberParams = {
  // (Mo, S, S) diagonal — Mo-S pair properties + 3-body for (Mo, S, S)
  // triple. Cutoff a·σ = 5.347·0.5985 ≈ 3.20 Å covers the Mo-S bond.
  // r23 = 3.82 excludes cross-prism S-S pairs.
  epsilon: 1.0,
  sigma: 0.59847,
  a: 5.3469681,
  lambda: 28.84616,
  gamma: 1.67093,
  cosTheta0: 0.13032373,
  A: 5.0,
  B: 131.687076,
  p: 4,
  q: 0,
  r23: 3.82,
};
const KANDEMIR16_S_MO_MO: StillingerWeberParams = {
  // (S, Mo, Mo) diagonal — same Mo-S pair properties (symmetric)
  // but the 3-body r23 = 4.27 (Mo-Mo cutoff, since k = Mo here).
  epsilon: 1.0,
  sigma: 0.59847,
  a: 5.3469681,
  lambda: 28.84616,
  gamma: 1.67093,
  cosTheta0: 0.13032373,
  A: 5.0,
  B: 131.687076,
  p: 4,
  q: 0,
  r23: 4.27,
};
const KANDEMIR16_S_S_S: StillingerWeberParams = {
  // 2-body S-S only. Cutoff a·σ = 3.8654·0.98826 ≈ 3.82 Å. Provides
  // a soft S-S repulsion at the close in-triangle distance of 3.16 Å.
  epsilon: 1.0,
  sigma: 0.98826,
  a: 3.86537956,
  lambda: 0,
  gamma: 0,
  cosTheta0: 0,
  A: 0.9641,
  B: 11.99905,
  p: 4,
  q: 0,
};
// Placeholder for the non-diagonal cross-triples (Mo-Mo-S, Mo-S-Mo,
// S-Mo-S, S-S-Mo). Kandemir 2016 leaves these all-zero — the angular
// contribution is captured entirely by the two diagonal rows above.
// Cutoffs set very small so the kernel always skips these via the
// rij ≥ a·σ early-out.
const KANDEMIR16_PLACEHOLDER: StillingerWeberParams = {
  epsilon: 1.0,
  sigma: 1.0,
  a: 0.1,
  lambda: 0,
  gamma: 0,
  cosTheta0: 0,
  A: 0,
  B: 0,
  p: 4,
  q: 0,
};

/**
 * Registry keyed by composition. Elemental tetrahedral semiconductors
 * use a single-element key; binaries use the sorted multi-element key,
 * same convention as the EAM and Tersoff tables.
 *
 * Experimental cohesion / lattice constant for sanity:
 *   Si   (diamond):    a₀ = 5.431 Å, E_coh = −4.63 eV/atom
 *   Ge   (diamond):    a₀ = 5.658 Å, E_coh = −3.85 eV/atom
 *   CdTe (zincblende): a₀ = 6.482 Å, E_coh = −2.06 eV/atom
 *   ZnS  (zincblende): a₀ = 5.410 Å, E_coh = −3.18 eV/atom
 *   ZnSe (zincblende): a₀ = 5.668 Å, E_coh = −2.71 eV/atom
 *   ZnTe (zincblende): a₀ = 6.103 Å, E_coh = −2.37 eV/atom
 *   β-HgS (zincblende): a₀ = 5.85 Å,  E_coh ≈ −1.83 eV/atom
 *   HgTe (zincblende): a₀ = 6.46 Å,  E_coh ≈ −1.72 eV/atom
 */
export const SW_PACKS: Record<string, SwPack> = {
  Si: {
    source: 'Stillinger & Weber (1985), Si — the canonical SW fit',
    build: () => new StillingerWeberPotential({ 'Si-Si-Si': SW_SI }),
  },
  Ge: {
    source: 'Ding & Andersen (1986), Ge — SW with refit ε, σ; angular params from Si',
    build: () => new StillingerWeberPotential({ 'Ge-Ge-Ge': SW_GE }),
  },
  'Cd,Te': {
    source: 'Zhou et al. (2013), PRB 88, 085309 — CdTe (from LAMMPS CdTeZnSeHgS0.sw)',
    build: () =>
      new StillingerWeberPotential({
        'Cd-Cd-Cd': ZHOU13_CD_CD_CD,
        'Cd-Cd-Te': ZHOU13_CD_CD_TE,
        'Cd-Te-Cd': ZHOU13_CD_TE_CD,
        'Cd-Te-Te': ZHOU13_CD_TE_TE,
        'Te-Cd-Cd': ZHOU13_TE_CD_CD,
        'Te-Cd-Te': ZHOU13_TE_CD_TE,
        'Te-Te-Cd': ZHOU13_TE_TE_CD,
        'Te-Te-Te': ZHOU13_TE_TE_TE,
      }),
  },
  'S,Zn': {
    source: 'Zhou et al. (2013), PRB 88, 085309 — ZnS (from LAMMPS CdTeZnSeHgS0.sw)',
    build: () =>
      new StillingerWeberPotential({
        'Zn-Zn-Zn': ZHOU13_ZN_ZN_ZN,
        'Zn-Zn-S': ZHOU13_ZN_ZN_S,
        'Zn-S-Zn': ZHOU13_ZN_S_ZN,
        'Zn-S-S': ZHOU13_ZN_S_S,
        'S-Zn-Zn': ZHOU13_S_ZN_ZN,
        'S-Zn-S': ZHOU13_S_ZN_S,
        'S-S-Zn': ZHOU13_S_S_ZN,
        'S-S-S': ZHOU13_S_S_S,
      }),
  },
  'Se,Zn': {
    source: 'Zhou et al. (2013), PRB 88, 085309 — ZnSe (from LAMMPS CdTeZnSeHgS0.sw)',
    build: () =>
      new StillingerWeberPotential({
        'Zn-Zn-Zn': ZHOU13_ZN_ZN_ZN,
        'Zn-Zn-Se': ZHOU13_ZN_ZN_SE,
        'Zn-Se-Zn': ZHOU13_ZN_SE_ZN,
        'Zn-Se-Se': ZHOU13_ZN_SE_SE,
        'Se-Zn-Zn': ZHOU13_SE_ZN_ZN,
        'Se-Zn-Se': ZHOU13_SE_ZN_SE,
        'Se-Se-Zn': ZHOU13_SE_SE_ZN,
        'Se-Se-Se': ZHOU13_SE_SE_SE,
      }),
  },
  'Te,Zn': {
    source: 'Zhou et al. (2013), PRB 88, 085309 — ZnTe (from LAMMPS CdTeZnSeHgS0.sw)',
    build: () =>
      new StillingerWeberPotential({
        'Zn-Zn-Zn': ZHOU13_ZN_ZN_ZN,
        'Zn-Zn-Te': ZHOU13_ZN_ZN_TE,
        'Zn-Te-Zn': ZHOU13_ZN_TE_ZN,
        'Zn-Te-Te': ZHOU13_ZN_TE_TE,
        'Te-Zn-Zn': ZHOU13_TE_ZN_ZN,
        'Te-Zn-Te': ZHOU13_TE_ZN_TE,
        'Te-Te-Zn': ZHOU13_TE_TE_ZN,
        'Te-Te-Te': ZHOU13_TE_TE_TE,
      }),
  },
  // β-HgS (metacinnabar, zincblende). NB the THERMODYNAMICALLY STABLE
  // phase at room temperature is α-HgS (cinnabar, trigonal P3₁21) — not
  // zincblende. Zhou's parameters target β-HgS specifically.
  'Hg,S': {
    source: 'Zhou et al. (2013), PRB 88, 085309 — HgS (from LAMMPS CdTeZnSeHgS0.sw)',
    build: () =>
      new StillingerWeberPotential({
        'Hg-Hg-Hg': ZHOU13_HG_HG_HG,
        'Hg-Hg-S': ZHOU13_HG_HG_S,
        'Hg-S-Hg': ZHOU13_HG_S_HG,
        'Hg-S-S': ZHOU13_HG_S_S,
        'S-Hg-Hg': ZHOU13_S_HG_HG,
        'S-Hg-S': ZHOU13_S_HG_S,
        'S-S-Hg': ZHOU13_S_S_HG,
        'S-S-S': ZHOU13_S_S_S,
      }),
  },
  // MoS₂ (monolayer or bulk 2H) — Kandemir et al. (2016), modified SW.
  // The r23 third-leg cutoff (handled by the StillingerWeberPotential
  // class's `r23` parameter) excludes the cross-prism S-S pairs that
  // would otherwise mismatch the single cosθ₀ = 0.13 (82°). For 2H
  // bulk MoS₂ with `layered: true`, this pack would need the interlayer
  // LJ overlay too — currently only the layered Tersoff FF runs the
  // overlay, so the SW MoS₂ path is single-layer accurate but doesn't
  // bind multiple sheets together. h-BN-style extension of the layered
  // FF to cover SW packs is a future addition.
  'Mo,S': {
    source: 'Kandemir et al. (2016), modified SW for MoS₂ (LAMMPS .swtmd)',
    build: () =>
      new StillingerWeberPotential({
        'Mo-Mo-Mo': KANDEMIR16_MO_MO_MO,
        'Mo-S-S': KANDEMIR16_MO_S_S,
        'S-Mo-Mo': KANDEMIR16_S_MO_MO,
        'S-S-S': KANDEMIR16_S_S_S,
        'Mo-Mo-S': KANDEMIR16_PLACEHOLDER,
        'Mo-S-Mo': KANDEMIR16_PLACEHOLDER,
        'S-Mo-S': KANDEMIR16_PLACEHOLDER,
        'S-S-Mo': KANDEMIR16_PLACEHOLDER,
      }),
  },

  // HgTe (coloradoite, zincblende). The narrow-gap semimetal end-member
  // of the HgCdTe MCT family used in infrared detectors.
  'Hg,Te': {
    source: 'Zhou et al. (2013), PRB 88, 085309 — HgTe (from LAMMPS CdTeZnSeHgS0.sw)',
    build: () =>
      new StillingerWeberPotential({
        'Hg-Hg-Hg': ZHOU13_HG_HG_HG,
        'Hg-Hg-Te': ZHOU13_HG_HG_TE,
        'Hg-Te-Hg': ZHOU13_HG_TE_HG,
        'Hg-Te-Te': ZHOU13_HG_TE_TE,
        'Te-Hg-Hg': ZHOU13_TE_HG_HG,
        'Te-Hg-Te': ZHOU13_TE_HG_TE,
        'Te-Te-Hg': ZHOU13_TE_TE_HG,
        'Te-Te-Te': ZHOU13_TE_TE_TE,
      }),
  },
};

/** Composition key for a crystal: sorted unique basis element symbols. */
const compositionKey = (crystal: CrystalState): string =>
  [...new Set(crystal.basis.map((b) => b.element))].sort().join(',');

/** The SW pack for `crystal`, or null if none is registered. */
export const swPackFor = (crystal: CrystalState): SwPack | null => SW_PACKS[compositionKey(crystal)] ?? null;

/**
 * The SW pack for a HYDROGEN-TERMINATED finite nanostructure: the pack of
 * its heavy elements, looked up with the capping hydrogens left out (the
 * kernel returns no pair or triple term for an unregistered element, so the
 * hydrogens are invisible to SW; the covalent force field holds them on
 * harmonic X–H bonds instead — see crystalForceFields.ts). Finite
 * nanostructures only: a bulk hydride is a molecular crystal of its own.
 */
export const cappedSwPackFor = (crystal: CrystalState): SwPack | null => {
  if (!crystal.finite && !crystal.nanostructure) return null;
  const elements = [...new Set(crystal.basis.map((b) => b.element))];
  if (!elements.includes('H')) return null;
  const heavy = elements.filter((e) => e !== 'H');
  if (heavy.length === 0) return null;
  return swPackFor({ ...crystal, basis: heavy.map((e) => ({ element: e, fx: 0, fy: 0, fz: 0, label: e })) });
};
